import {
  DIET_LABEL_OPTIONS,
  HEALTH_LABEL_OPTIONS,
} from '@/constants/dietary-goals';
import {
  getRecipeIntoleranceTags,
  getValidAllergies,
  getValidCuisineTypes,
  getValidFoodIntolerances,
  getValidPreferences,
} from '@/lib/clientPreferenceOptions';
import { MEDICAL_CONDITIONS_OPTIONS } from '@/constants/medical-conditions';
import { formatDisplayLabel } from '@/utils/display-labels';

const NO_ANSWER_PROVIDED = 'No answer provided';

type AnswerDisplayOption = {
  label?: string | null;
  name?: string | null;
  value?: string | null;
  id?: string | null;
  key?: string | null;
};

interface FormatQuestionnaireAnswerOptions {
  type?: string;
  otherText?: string;
  options?: readonly AnswerDisplayOption[];
}

const ACTIVITY_LEVEL_OPTIONS: readonly AnswerDisplayOption[] = [
  { value: 'sedentary', label: 'Sedentary' },
  { value: 'lightly_active', label: 'Lightly Active' },
  { value: 'low_active', label: 'Lightly Active' },
  { value: 'light_active', label: 'Lightly Active' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'moderately_active', label: 'Moderately Active' },
  { value: 'active', label: 'Active' },
  { value: 'very_active', label: 'Very Active' },
  { value: 'highly_active', label: 'Very Active' },
  { value: 'extremely_active', label: 'Extra Active' },
  { value: 'extra_active', label: 'Extra Active' },
  { value: 'athlete_boys', label: 'Athlete - Boys' },
  { value: 'athlete_girls', label: 'Athlete - Girls' },
];

function getKnownDisplayOptions(): readonly AnswerDisplayOption[] {
  return [
    ...getValidAllergies(),
    ...getValidFoodIntolerances(),
    ...getRecipeIntoleranceTags(),
    ...getValidPreferences(),
    ...getValidCuisineTypes(),
    ...HEALTH_LABEL_OPTIONS,
    ...DIET_LABEL_OPTIONS,
    ...MEDICAL_CONDITIONS_OPTIONS,
    ...ACTIVITY_LEVEL_OPTIONS,
  ];
}

function normalizeLookupKey(value: unknown): string {
  return String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

function cleanLabel(value: unknown): string {
  const trimmed = String(value ?? '').replace(/\s+/g, ' ').trim();
  if (!trimmed) return '';
  return trimmed.includes('_') ? formatDisplayLabel(trimmed) : trimmed;
}

function addOptionLabel(map: Map<string, string>, option: AnswerDisplayOption): void {
  const label = cleanLabel(option.label ?? option.name ?? option.value ?? option.id ?? option.key);
  if (!label) return;

  [option.value, option.id, option.key, option.name, option.label]
    .map(normalizeLookupKey)
    .filter(Boolean)
    .forEach((key) => {
      if (!map.has(key)) map.set(key, label);
    });
}

function buildKnownLabels(): Map<string, string> {
  const map = new Map<string, string>();
  getKnownDisplayOptions().forEach((option) => addOptionLabel(map, option));
  return map;
}

function buildLabelLookup(options?: readonly AnswerDisplayOption[]): Map<string, string> {
  const map = new Map<string, string>();
  options?.forEach((option) => addOptionLabel(map, option));
  buildKnownLabels().forEach((label, key) => {
    if (!map.has(key)) map.set(key, label);
  });
  return map;
}

function normalizeType(type?: string): string {
  return String(type ?? '').trim().toLowerCase().replace(/_/g, '-');
}

function isOptionType(type?: string): boolean {
  const normalized = normalizeType(type);
  return normalized === 'select'
    || normalized === 'multi-select'
    || normalized === 'radio'
    || normalized === 'checkbox';
}

function isFreeTextType(type?: string): boolean {
  const normalized = normalizeType(type);
  return normalized === 'text' || normalized === 'textarea';
}

function looksLikeStoredCode(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (trimmed.includes('_')) return true;
  if (/^\d+(?:-\d+)+$/.test(trimmed)) return false;
  return /^[a-z0-9]+(?:-[a-z0-9]+)+$/i.test(trimmed);
}

function formatAnswerToken(value: unknown, labelLookup: Map<string, string>): string {
  const trimmed = String(value ?? '').replace(/\s+/g, ' ').trim();
  if (!trimmed) return '';

  const mappedLabel = labelLookup.get(normalizeLookupKey(trimmed));
  if (mappedLabel) return mappedLabel;

  return looksLikeStoredCode(trimmed) ? formatDisplayLabel(trimmed) : trimmed;
}

function formatAnswerString(value: string, type: string | undefined, labelLookup: Map<string, string>): string {
  const trimmed = value.trim();
  if (!trimmed) return NO_ANSWER_PROVIDED;

  if (trimmed.includes(',') && isOptionType(type)) {
    const formattedParts = trimmed
      .split(',')
      .map((part) => formatAnswerToken(part, labelLookup))
      .filter(Boolean);
    return formattedParts.length > 0 ? formattedParts.join(', ') : NO_ANSWER_PROVIDED;
  }

  if (isFreeTextType(type) && !labelLookup.has(normalizeLookupKey(trimmed))) {
    return trimmed;
  }

  return formatAnswerToken(trimmed, labelLookup) || NO_ANSWER_PROVIDED;
}

function formatAnswerPrimitive(value: unknown, type: string | undefined, labelLookup: Map<string, string>): string {
  if (typeof value === 'string') {
    return formatAnswerString(value, type, labelLookup);
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : NO_ANSWER_PROVIDED;
  }

  if (typeof value === 'boolean') {
    return normalizeType(type) === 'checkbox'
      ? (value ? 'Yes, confirmed' : 'No')
      : value ? 'Yes' : 'No';
  }

  return value == null ? NO_ANSWER_PROVIDED : String(value);
}

function formatObjectAnswer(answer: Record<string, unknown>, type: string | undefined, labelLookup: Map<string, string>): string {
  const rawValue = answer.value;
  const rawUnit = answer.unit;

  if (Array.isArray(rawValue)) {
    const formattedValue = rawValue
      .map((item) => formatAnswerPrimitive(item, type, labelLookup))
      .filter((item) => item && item !== NO_ANSWER_PROVIDED)
      .join(', ');
    return formattedValue || NO_ANSWER_PROVIDED;
  }

  if (typeof rawValue === 'string' || typeof rawValue === 'number' || typeof rawValue === 'boolean') {
    const formattedValue = formatAnswerPrimitive(rawValue, type, labelLookup);
    const unit = typeof rawUnit === 'string' ? rawUnit.trim() : '';
    return unit && formattedValue !== NO_ANSWER_PROVIDED ? `${formattedValue} ${unit}` : formattedValue;
  }

  const fallbackValue = answer.label ?? answer.name ?? answer.id ?? answer.key;
  if (typeof fallbackValue === 'string' || typeof fallbackValue === 'number' || typeof fallbackValue === 'boolean') {
    return formatAnswerPrimitive(fallbackValue, type, labelLookup);
  }

  const entries = Object.entries(answer)
    .map(([key, value]) => {
      const formattedValue = formatQuestionnaireAnswerValue(value, { type });
      if (!formattedValue || formattedValue === NO_ANSWER_PROVIDED) return '';
      return `${formatDisplayLabel(key)}: ${formattedValue}`;
    })
    .filter(Boolean);

  return entries.length > 0 ? entries.join(', ') : NO_ANSWER_PROVIDED;
}

function formatOtherAnswerText(value: string, labelLookup: Map<string, string>): string {
  return value
    .split(',')
    .map((part) => {
      const trimmed = part.trim();
      if (!trimmed) return '';
      return looksLikeStoredCode(trimmed) || labelLookup.has(normalizeLookupKey(trimmed))
        ? formatAnswerToken(trimmed, labelLookup)
        : trimmed.replace(/_+/g, ' ');
    })
    .filter(Boolean)
    .join(', ');
}

export function formatQuestionnaireAnswerValue(
  answer: unknown,
  { type, otherText, options }: FormatQuestionnaireAnswerOptions = {}
): string {
  const labelLookup = buildLabelLookup(options);
  let main = NO_ANSWER_PROVIDED;

  if (answer != null) {
    if (Array.isArray(answer)) {
      const formattedItems = answer
        .map((item) => formatAnswerPrimitive(item, type, labelLookup))
        .filter((item) => item && item !== NO_ANSWER_PROVIDED);
      main = formattedItems.length > 0 ? formattedItems.join(', ') : NO_ANSWER_PROVIDED;
    } else if (typeof answer === 'object') {
      main = formatObjectAnswer(answer as Record<string, unknown>, type, labelLookup);
    } else {
      main = formatAnswerPrimitive(answer, type, labelLookup);
    }
  }

  const otherAnswer = typeof otherText === 'string' ? otherText.trim() : '';
  if (otherAnswer) {
    const formattedOtherAnswer = formatOtherAnswerText(otherAnswer, labelLookup);
    return main !== NO_ANSWER_PROVIDED ? `${main} - ${formattedOtherAnswer}` : formattedOtherAnswer;
  }

  return main;
}
